import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError, ROUTER_PATHS } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,
  IEstadoInstalacion,
  IPreagendamiento,
  PreagendamientosPagingRes,
  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { isAxiosError } from 'axios';

const { get, post, put, remove } = erpApi();

///* tanStack query
export const useFetchPreagendamientos = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetPreagendamientosParams> = {}) => {
  return useQuery({
    queryKey: ['preagendamientos', ...Object.values(params ?? {})],
    queryFn: () => getPreagendamientos(params),
    enabled: enabled,
    refetchOnWindowFocus: false,
  });
};

export const useGetPreagendamiento = (id: string) => {
  return useQuery({
    queryKey: ['preagendamiento', id],
    queryFn: () => getPreagendamiento(id),
    retry: false,
  });
};

export const useCreatePreagendamiento = <T>({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: (params: CreatePreagendamientoParams<T>) =>
      createPreagendamiento(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['preagendamientos'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Preagendamiento creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {

      if (isAxiosError(error)) {
        const respError: any = error?.response?.data || {};
        switch (respError.code) {
          case 408:
            navigate && navigate(`${ROUTER_PATHS.ventas.preAgendamientoNav}`)
            setAlert(true, 'El preagendamiento se ha creado correctamente. [408]', AlertSeverityEnum.success)
            break
          default:

            enableErrorRedirect &&
              navigate &&
              returnUrl &&
              navigate(returnErrorUrl ?? (returnUrl || ''));

            handleAxiosError(error, customMessageErrorAlert)
            break;
        }
      }

      /* enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert); */
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};
export const useUpdatePreagendamiento = <T>({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,

  customSuccessCallback,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: (params: UpdatePreagendamientoParams<T>) =>
      updatePreagendamiento(params),

    onSuccess: res => {
      queryClient.invalidateQueries({ queryKey: ['preagendamientos'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Preagendamiento actualizada correctamente',
          AlertSeverityEnum.success
        );

      customSuccessCallback && customSuccessCallback(res?.data);
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useDeletePreagendamiento = () => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: deletePreagendamiento,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['preagendamientos'] });
      setAlert(
        true,
        'Preagendamiento eliminada correctamente',
        AlertSeverityEnum.success
      );
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
    onError: error => {
      handleAxiosError(error);
    },
  });
};

///* axios
type GetPreagendamientosParams = Partial<IPreagendamiento> & {
  page?: number;
  page_size?: number;
  fecha_creacion_after?: string | null;
  fecha_creacion_before?: string | null;
  filterByState?: boolean;
  identificacion?: string;
  user_create?: string;
  estado_aceptacion?: boolean;
  estado_preagendamiento_in?: string;
  es_pyme?: boolean;

};

export type CreatePreagendamientoParamsForm = Omit<
  IPreagendamiento,
  'id_preagendamiento'
>;

export type CreatePreagendamientoParams<T> = T;
export type UpdatePreagendamientoParams<T> = {
  id: string;
  data: T;
};

export const getPreagendamientos = async (
  params?: GetPreagendamientosParams
) => {
  const stateParams = { ...params };

  // filterByState
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);

  return get<PreagendamientosPagingRes>(
    `${urlBackend}api/v1/preagendamiento/?${queryParams}`,
    true
  );
};

export const getPreagendamiento = async (id: string) => {
  try {
    return await get<IPreagendamiento>(
      `${urlBackend}api/v1/preagendamiento/${id}/`,
      true
    );
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createPreagendamiento = async <T>(
  data: CreatePreagendamientoParams<T>
) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<IPreagendamiento>(
    `${urlBackend}api/v1/preagendamiento/`,
    data,
    true
  );
};

export const updatePreagendamiento = async <T>({
  id,
  data,
}: UpdatePreagendamientoParams<T>) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<IPreagendamiento>(
    `${urlBackend}api/v1/preagendamiento/${id}/`,
    data,
    true
  );
};

export const deletePreagendamiento = async (id: string) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  setIsGlobalLoading(true);
  return remove<IPreagendamiento>(
    `${urlBackend}api/v1/preagendamiento/${id}/`,
    true
  );
};


///* Estado del controto
export const useRevisarContrato = ({ queryKey = 'contrato' }: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: postRevisarContrato,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      setAlert(
        true,
        'Contrato fue revisado.',
        AlertSeverityEnum.success
      );
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
    onError: error => {
      handleAxiosError(error);
    },
  });
};

export const postRevisarContrato = async (id_preagendamiento: string) => {
  /* const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  setIsGlobalLoading(true); */
  return post<any>(
    `${urlBackend}api/v1/enviar-revisar-contrato/${id_preagendamiento}/`, {},
    true
  );
};

///* validar instalacion realizada con sofnet


/* */

export const getInstalacionSofnet = async (id: string) => {
  try {
    return await get<IEstadoInstalacion>(
      `${urlBackend}api/v1/obtener-estado-instalacion-sofnet/${id}`,
      true
    );
  } catch (error) {
    handleAxiosError(error);
  }
};